import { useEffect, useRef, useState } from 'react';
import { Check, Archive, AlertCircle, Users, Clock, CircleCheck, Eye, GripVertical, MessageSquareText } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { useMonedaDeLaCasa } from '@/hooks/useMonedaDeLaCasa';
import { cn } from '@/lib/cn';
import type { Asunto } from '@/api/asuntos.api';
import { CAJON_COLOR, PRIORIDAD_TONO, PRIORIDAD_NOMBRE, hora12, fechaCorta, plata, temaLegible } from '@/lib/athena';
import { accionPrincipal, type AccionPrincipal } from '@/lib/metoca';

/**
 * UNA TARJETA DE ASUNTO, con gestos.
 *
 * Mandato: deslizar a la derecha aprueba (verde), a la izquierda descarta
 * (gris). Se usa igual en Hoy, en Me toca y en la ficha de una persona: una
 * sola tarjeta, o las tres pantallas se separan solas.
 *
 * ═══ POR QUÉ EL GESTO SE IMPLEMENTA A MANO ═══
 *
 * Con eventos de puntero, sin librería. Una librería de gestos para esto son
 * 40 KB que el teléfono descarga para resolver un arrastre horizontal.
 *
 * Tres detalles que hacen que se sienta bien y que son la diferencia entre un
 * gesto y un accidente:
 *
 *  1. **Umbral de 72 px.** Menos que eso y un scroll medio torcido dispararía
 *     la acción. La tarjeta vuelve sola a su sitio si no se llega.
 *  2. **Se decide el eje una sola vez.** Si el primer movimiento es más
 *     vertical que horizontal, es scroll y la tarjeta no se mueve más. Sin
 *     esto, la lista se traba al intentar bajar.
 *  3. **El fondo aparece mientras se arrastra**, así se ve qué va a pasar
 *     antes de soltar. Un gesto sin respuesta previa es una apuesta.
 *
 * ═══ EL BOTÓN PRINCIPAL DICE LO QUE HACE ═══
 *
 * «Aprobar» una suposición de la máquina; «Hecho» lo ya confirmado. La acción
 * la decide la tarjeta (`accionPrincipal`) y se la entrega a la pantalla: antes
 * cada pantalla mandaba `aprobar` por su cuenta, también bajo el rótulo
 * «Hecho», y nada quedaba cumplido nunca.
 *
 * ═══ UN MENSAJE PARA EL CLIENTE NO SE APRUEBA AQUÍ ═══
 *
 * Los borradores de Seguimientos salen como un asunto más, y aprobar uno lo
 * manda por WhatsApp. Esta tarjeta no enseña el texto: para ellos el botón es
 * «Revisar el mensaje» (lleva a Seguimientos) y deslizar a la derecha no hace
 * nada. El servidor tampoco deja aprobarlos desde aquí.
 */

interface Props {
  asunto: Asunto;
  onAbrir: (id: string) => void;
  /**
   * El botón principal y deslizar a la derecha, con la acción que toca según el asunto.
   * `revisar` solo llega cuando la pantalla puso su propio rótulo (y entonces no lo mira).
   */
  onPrincipal?: (id: string, accion: AccionPrincipal) => void;
  /** Cuando la pantalla hace otra cosa con el botón principal (en «Ruido», rescatar). */
  rotuloPrincipal?: string;
  /**
   * Ir a Seguimientos a leer un mensaje para el cliente. Lo pasa la pantalla solo si quien mira
   * tiene Seguimientos en su menú; sin esto, la tarjeta de un borrador no trae botón principal.
   */
  onRevisar?: () => void;
  onDescartar?: (id: string) => void;
  /** Mantener presionado / clic derecho. Sin esto, el menú no aparece. */
  onPosponer?: (id: string, hasta: string) => void;
  onVerMensaje?: (a: Asunto) => void;
  /** `false` en listas donde deslizar no aplica (ya cerradas). */
  conGestos?: boolean;
  /** Muestra la fecha completa en vez de solo la hora. */
  conFecha?: boolean;
  /**
   * Para una columna angosta (el tablero de Me toca): la fecha va arriba y el
   * botón principal queda a la vista, sin abrir el menú.
   */
  compacta?: boolean;
  /** En la compacta: se puede arrastrar, y lleva su agarradero. */
  agarre?: boolean;
}

const UMBRAL = 72;

const ROTULO: Record<AccionPrincipal, string> = { aprobar: 'Aprobar', hecho: 'Hecho', revisar: 'Revisar el mensaje' };
const ICONO: Record<AccionPrincipal, typeof Check> = { aprobar: Check, hecho: CircleCheck, revisar: Eye };

export function TarjetaDeAsunto({
  asunto: a,
  onAbrir,
  onPrincipal,
  rotuloPrincipal,
  onRevisar,
  onDescartar,
  onPosponer,
  onVerMensaje,
  conGestos = true,
  conFecha = false,
  compacta = false,
  agarre = false,
}: Props) {
  const [dx, setDx] = useState(0);
  const [saliendo, setSaliendo] = useState<'izq' | 'der' | null>(null);
  const [menu, setMenu] = useState(false);
  const inicio = useRef<{ x: number; y: number } | null>(null);
  const eje = useRef<'indeciso' | 'x' | 'y'>('indeciso');
  const presion = useRef<ReturnType<typeof setTimeout> | null>(null);

  // MANTENER PRESIONADO = menú rápido. 450 ms: menos que eso y un toque normal
  // lo dispara; más, y la gente suelta antes de que pase algo.
  const cancelarPresion = () => {
    if (presion.current) clearTimeout(presion.current);
    presion.current = null;
  };

  const color = CAJON_COLOR[a.cajon];
  const casa = useMonedaDeLaCasa();
  const principal = accionPrincipal(a);
  const rotulo = rotuloPrincipal ?? ROTULO[principal];
  const IconoPrincipal = ICONO[principal];
  // Si la pantalla puso su propio botón (en «Ruido», rescatar), ese manda también sobre un borrador.
  const esMensaje = principal === 'revisar' && !rotuloPrincipal;
  const alPrincipal = esMensaje ? onRevisar : onPrincipal && (() => onPrincipal(a.id, principal));

  // Deslizar saca la tarjeta de la vista antes de avisar. Si el asunto SIGUE en
  // la lista (aprobarlo no lo quita), tiene que volver: sin esto quedaba una
  // fila en blanco. Vuelve al cambiar de estado, y a los 2,5 s si no cambió
  // nada (el servidor falló o tarda): mejor verla de nuevo que perderla.
  const regreso = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    setSaliendo(null);
    setDx(0);
  }, [a.estado]);
  useEffect(() => () => void (regreso.current && clearTimeout(regreso.current)), []);
  const volverSiSigue = () => {
    if (regreso.current) clearTimeout(regreso.current);
    regreso.current = setTimeout(() => {
      setSaliendo(null);
      setDx(0);
    }, 2500);
  };

  // Con el mouse, soltar después de deslizar es además un clic: sin esto,
  // deslizar aprobaba y de paso abría la hoja encima.
  const deslizo = useRef(false);

  const alBajar = (e: React.PointerEvent) => {
    if (!conGestos) return;
    inicio.current = { x: e.clientX, y: e.clientY };
    eje.current = 'indeciso';
    deslizo.current = false;
    cancelarPresion();
    presion.current = setTimeout(() => {
      // El menú cancela el arrastre: si no, al soltar se dispararía además la
      // acción del deslizamiento y el dueño vería dos cosas por un gesto.
      inicio.current = null;
      setDx(0);
      setMenu(true);
    }, 450);
  };

  const alMover = (e: React.PointerEvent) => {
    if (!inicio.current) return;
    const dX = e.clientX - inicio.current.x;
    const dY = e.clientY - inicio.current.y;

    // El eje se decide UNA vez, con el primer movimiento claro. Si no, la
    // lista se traba cada vez que alguien baja con el dedo medio torcido.
    if (eje.current === 'indeciso') {
      if (Math.abs(dX) < 8 && Math.abs(dY) < 8) return;
      eje.current = Math.abs(dX) > Math.abs(dY) ? 'x' : 'y';
    }
    if (eje.current === 'y') return;
    // Moverse cancela el "mantener": arrastrar y mantener son gestos distintos.
    cancelarPresion();
    deslizo.current = true;
    // Un mensaje para el cliente no se desliza a la derecha: no hay nada que prometerle al dedo.
    setDx(esMensaje ? Math.min(0, dX) : dX);
  };

  const alSoltar = () => {
    cancelarPresion();
    if (!inicio.current) return;
    const movido = dx;
    inicio.current = null;
    eje.current = 'indeciso';

    if (movido > UMBRAL && onPrincipal && !esMensaje) {
      setSaliendo('der');
      // Se deja ver la animación antes de avisar al padre: si la fila
      // desaparece de golpe, no se entiende qué pasó.
      setTimeout(() => onPrincipal(a.id, principal), 180);
      volverSiSigue();
      return;
    }
    if (movido < -UMBRAL && onDescartar) {
      setSaliendo('izq');
      setTimeout(() => onDescartar(a.id), 180);
      volverSiSigue();
      return;
    }
    setDx(0);
  };

  const monto = plata(a.montoCentavos, a.moneda, casa);

  return (
    // En la compacta la franja del cajón es de la tarjeta entera (cuerpo y botón), no solo del cuerpo.
    <div className={cn('relative overflow-hidden', compacta && ['border-l-4', color.borde, a.esSuposicion && 'border-dashed'])}>
      {/* El fondo dice qué va a pasar, ANTES de soltar. */}
      {conGestos && dx !== 0 && (
        <div
          className={cn(
            'absolute inset-0 flex items-center px-5 text-sm font-medium',
            dx > 0
              ? 'justify-start bg-emerald-500 text-white'
              : 'justify-end bg-slate-400 text-white',
          )}
        >
          {dx > 0 ? (
            <span className="flex items-center gap-1.5">
              <Check className="h-4 w-4" /> {rotulo}
            </span>
          ) : (
            <span className="flex items-center gap-1.5">
              <Archive className="h-4 w-4" /> Descartar
            </span>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => {
          if (deslizo.current) deslizo.current = false;
          else onAbrir(a.id);
        }}
        onPointerDown={alBajar}
        onPointerMove={alMover}
        onPointerUp={alSoltar}
        onPointerCancel={alSoltar}
        onContextMenu={(e) => {
          if (!conGestos) return;
          e.preventDefault();
          setMenu(true);
        }}
        style={{
          transform: saliendo
            ? `translateX(${saliendo === 'der' ? 400 : -400}px)`
            : `translateX(${dx}px)`,
          opacity: saliendo ? 0 : 1,
          // Sin gestos no se le quita al navegador el desplazamiento de lado:
          // es con lo que se pasa de una columna a otra del tablero en el teléfono.
          touchAction: conGestos ? 'pan-y' : undefined,
        }}
        className={cn(
          'relative flex w-full bg-white text-left transition-[transform,opacity] duration-150 dark:bg-slate-900',
          'active:bg-slate-50 dark:active:bg-slate-800/60',
          'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-600',
          compacta
            ? 'flex-col gap-1 px-2.5 pb-1.5 pt-2.5'
            : [
                'border-b border-b-slate-100 last:border-b-0 dark:border-b-slate-800',
                // 44 px mínimos de zona de toque, como pide el mandato.
                'min-h-[64px] gap-3 border-l-4 px-3 py-3',
                color.borde,
                a.esSuposicion && 'border-dashed',
              ],
        )}
      >
        <span className={cn('whitespace-nowrap text-xs text-slate-500 dark:text-slate-400', !compacta && 'min-w-[68px] shrink-0 pt-0.5')}>
          {compacta
            ? `${fechaCorta(a.fechaCompromiso)} · ${hora12(a.fechaCompromiso)}`
            : conFecha
              ? fechaCorta(a.fechaCompromiso)
              : hora12(a.fechaCompromiso)}
        </span>

        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium leading-snug text-slate-900 dark:text-slate-100">
            {a.descripcion}
          </span>
          <span className="mt-0.5 block truncate text-xs text-slate-500 dark:text-slate-400">
            {a.conQuien}
            {a.tema ? ` · ${temaLegible(a.tema)}` : ''}
            {monto ? ` · ${monto}` : ''}
          </span>

          <span className="mt-1 flex flex-wrap items-center gap-1">
            {a.vencido && (
              <Badge className="bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200">
                Vencido
              </Badge>
            )}
            {a.prioridad === 'ALTA' && !a.vencido && (
              <Badge className={PRIORIDAD_TONO.ALTA}>{PRIORIDAD_NOMBRE.ALTA}</Badge>
            )}
            {a.confianza === 'DUDOSO' && (
              <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-300">
                <AlertCircle className="h-3 w-3" /> Dudoso
              </span>
            )}
            {a.esBorrador && (
              <span className="flex items-center gap-1 text-xs font-medium text-brand-700 dark:text-brand-300">
                <MessageSquareText aria-hidden className="h-3 w-3" /> Mensaje para el cliente
              </span>
            )}
            {a.vistoEnChats > 1 && (
              <span className="flex items-center gap-1 text-xs text-slate-400">
                <Users className="h-3 w-3" /> {a.vistoEnChats} chats
              </span>
            )}
            {a.responsable && (
              <span className="text-xs text-slate-400">→ {a.responsable.nombre}</span>
            )}
          </span>
        </span>
      </button>

      {/* EN LA COMPACTA, EL BOTÓN PRINCIPAL A LA VISTA: en el tablero no hay
          deslizar (de lado se cambia de columna) ni menú. */}
      {compacta && alPrincipal && (
        <div className="flex items-center justify-end gap-2 bg-white px-2 pb-2 dark:bg-slate-900">
          {agarre && (
            <span
              title="Arrástrala a «Por hacer» para aprobarla"
              className="mr-auto hidden cursor-grab text-slate-300 md:block dark:text-slate-600"
            >
              <GripVertical aria-hidden className="h-4 w-4" />
            </span>
          )}
          <button
            type="button"
            onClick={alPrincipal}
            className={cn(
              'inline-flex min-h-[44px] items-center gap-1.5 rounded-md px-3 text-xs font-semibold transition-colors md:min-h-[32px]',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-600',
              rotuloPrincipal
                ? 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                : principal === 'hecho'
                  ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-200 dark:hover:bg-emerald-900/50'
                  : 'bg-brand-50 text-brand-700 hover:bg-brand-100 dark:bg-brand-900/30 dark:text-brand-200 dark:hover:bg-brand-900/50',
            )}
          >
            <IconoPrincipal aria-hidden className="h-3.5 w-3.5" /> {rotulo}
          </button>
        </div>
      )}

      {/* EL MENÚ RÁPIDO. Mantener presionado en móvil, clic derecho en
          escritorio. Lo que hay acá es lo que se hace sin abrir la hoja:
          posponer con tres plazos y saltar al mensaje. */}
      {menu && (
        <div className="flex flex-wrap gap-1.5 border-b border-slate-100 bg-slate-50 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/40">
          {alPrincipal && (
            <button
              onClick={() => {
                setMenu(false);
                alPrincipal();
              }}
              className="flex min-h-[36px] items-center gap-1 rounded-md bg-white px-2.5 text-xs dark:bg-slate-900"
            >
              <IconoPrincipal className="h-3.5 w-3.5" />
              {rotulo}
            </button>
          )}
          {onPosponer &&
            [
              { et: 'Esta tarde', h: 6 },
              { et: 'Mañana', h: 24 },
              { et: 'El lunes', h: 72 },
            ].map((o) => (
              <button
                key={o.et}
                onClick={() => {
                  setMenu(false);
                  onPosponer(a.id, new Date(Date.now() + o.h * 3_600_000).toISOString());
                }}
                className="flex min-h-[36px] items-center gap-1 rounded-md bg-white px-2.5 text-xs dark:bg-slate-900"
              >
                <Clock className="h-3.5 w-3.5" /> {o.et}
              </button>
            ))}
          {/* Lo anotado a mano no salió de ningún chat: no hay mensaje al que saltar. */}
          {onVerMensaje && a.conversationId && (
            <button
              onClick={() => {
                setMenu(false);
                onVerMensaje(a);
              }}
              className="flex min-h-[36px] items-center gap-1 rounded-md bg-white px-2.5 text-xs dark:bg-slate-900"
            >
              <MessageSquareText className="h-3.5 w-3.5" /> Ver mensaje
            </button>
          )}
          {onDescartar && (
            <button
              onClick={() => {
                setMenu(false);
                onDescartar(a.id);
              }}
              className="flex min-h-[36px] items-center gap-1 rounded-md bg-white px-2.5 text-xs text-slate-500 dark:bg-slate-900"
            >
              <Archive className="h-3.5 w-3.5" /> Descartar
            </button>
          )}
          <button
            onClick={() => setMenu(false)}
            className="min-h-[36px] px-2 text-xs text-slate-400"
          >
            Cerrar
          </button>
        </div>
      )}
    </div>
  );
}
